import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { onEnterOrSpace } from './subjectSiteMenu';

/**
 * PopupParent and PopupParentV2 sit over CoverLayer1; PopupParentV3 sits over
 * CoverLayer2, above them, so it can open on top of another popup
 */
export type SubjectSitePopupVariant =
  | 'PopupParent'
  | 'PopupParentV2'
  | 'PopupParentV3';

type Props = {
  variant: SubjectSitePopupVariant;
  isOpen: boolean;
  onClose: () => void;
  /**
   * The popup's maximum width in pixels
   */
  width?: number;
  closeLabel?: string;
  children?: React.ReactNode;
};

const FADE_MILLISECONDS = 200;
const MINIMUM_TOP_MARGIN = 20;

/**
 * One of the subject site's popups: faded in over a cover layer, centred in the
 * window (at least 20px from its top), closed by its button, the cover or Escape
 */
const SubjectSitePopup: React.FC<Props> = ({
  variant,
  isOpen,
  onClose,
  width,
  closeLabel = 'Close',
  children,
}) => {
  const popupRef = useRef<HTMLDivElement>(null);
  const [bShown, setBShown] = useState(isOpen);
  const [bVisible, setBVisible] = useState(false);
  const [top, setTop] = useState(0);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    if (isOpen) {
      setBShown(true);
      return;
    }
    setBVisible(false);
    const timeout = window.setTimeout(
      () => setBShown(false),
      FADE_MILLISECONDS
    );
    return () => window.clearTimeout(timeout);
  }, [isOpen]);

  useLayoutEffect(() => {
    if (!isOpen || !bShown) return;

    const position = () => {
      const popupHeight = popupRef.current?.offsetHeight ?? 0;
      setTop(
        window.scrollY +
          Math.max((window.innerHeight - popupHeight) / 2, MINIMUM_TOP_MARGIN)
      );
    };
    position();
    setBVisible(true);

    let previousWidth = document.body.clientWidth;
    const positionOnWidthChange = () => {
      if (document.body.clientWidth === previousWidth) return;
      previousWidth = document.body.clientWidth;
      position();
    };
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onCloseRef.current();
    };
    window.addEventListener('resize', positionOnWidthChange);
    document.addEventListener('keyup', closeOnEscape);
    return () => {
      window.removeEventListener('resize', positionOnWidthChange);
      document.removeEventListener('keyup', closeOnEscape);
    };
  }, [isOpen, bShown]);

  if (!bShown) return null;

  const coverLayer =
    variant === 'PopupParentV3' ? 'CoverLayer2' : 'CoverLayer1';
  const visibleClass = bVisible ? ' Visible' : '';

  return (
    <>
      <div
        className={`${coverLayer}${visibleClass}`}
        style={{ display: 'block' }}
        onClick={onClose}
      ></div>
      <div
        className={`${variant}${visibleClass}`}
        style={{ display: 'block', top }}
        role="dialog"
        aria-modal="true"
      >
        <div
          ref={popupRef}
          className="Popup"
          style={width ? { maxWidth: width } : undefined}
        >
          <div className="ActualContent">{children}</div>
          <div
            className="ClosePopup"
            role="button"
            tabIndex={0}
            aria-label={closeLabel}
            onClick={onClose}
            onKeyDown={onEnterOrSpace(onClose)}
          ></div>
        </div>
      </div>
    </>
  );
};

export default SubjectSitePopup;
